import React, { useEffect, useRef, useState } from "react";
import { X, Terminal as TerminalIcon, Wifi, WifiOff } from "lucide-react";
import { Terminal } from "xterm";
import { FitAddon } from "xterm-addon-fit";
import "xterm/css/xterm.css";

// In produzione REACT_APP_BACKEND_URL='' → usa l'origin corrente per il WebSocket
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

const MIN_HEIGHT = 200;
const DEFAULT_HEIGHT = 380;
const HEIGHT_STORAGE_KEY = "warthive_terminal_height";

export default function WebTerminalModal({ rigId, rigName, open, onClose, initialCommand }) {
  const containerRef = useRef(null);
  const termRef = useRef(null);
  const fitRef = useRef(null);
  const wsRef = useRef(null);
  const sentInitialRef = useRef(false);
  const dragStateRef = useRef(null);
  const [status, setStatus] = useState("connecting"); // connecting | connected | disconnected | error
  const [panelHeight, setPanelHeight] = useState(() => {
    const saved = parseInt(localStorage.getItem(HEIGHT_STORAGE_KEY), 10);
    return Number.isFinite(saved) && saved >= MIN_HEIGHT ? saved : DEFAULT_HEIGHT;
  });

  useEffect(() => {
    if (!open) return;
    if (!containerRef.current) return;

    const term = new Terminal({
      cursorBlink: true,
      fontFamily: "IBM Plex Mono, Menlo, monospace",
      fontSize: 13,
      lineHeight: 1.25,
      theme: {
        background: "#0B0B0F",
        foreground: "#FAFAFA",
        cursor: "#10B981",
        cursorAccent: "#0B0B0F",
        black: "#000000",
        red: "#EF4444",
        green: "#10B981",
        yellow: "#F59E0B",
        blue: "#3B82F6",
        magenta: "#A78BFA",
        cyan: "#22D3EE",
        white: "#E4E4E7",
        brightBlack: "#52525B",
        brightRed: "#F87171",
        brightGreen: "#34D399",
        brightYellow: "#FBBF24",
        brightBlue: "#60A5FA",
        brightMagenta: "#C4B5FD",
        brightCyan: "#67E8F9",
        brightWhite: "#FAFAFA",
        selectionBackground: "rgba(16, 185, 129, 0.3)",
      },
      allowProposedApi: true,
    });
    const fit = new FitAddon();
    term.loadAddon(fit);
    term.open(containerRef.current);

    termRef.current = term;
    fitRef.current = fit;

    setTimeout(() => {
      try { fit.fit(); } catch (e) { /* noop */ }
    }, 100);

    const token = localStorage.getItem("warthive_token") || "";
    const wsUrl =
      BACKEND_URL.replace(/^https/, "wss").replace(/^http/, "ws") +
      `/api/terminal/${rigId}?token=${encodeURIComponent(token)}`;

    term.write("\x1b[2m· connessione a " + rigName + "…\x1b[0m\r\n");

    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;

    ws.onopen = () => {
      setStatus("connected");
      // send initial resize
      try {
        const { cols, rows } = term;
        ws.send(JSON.stringify({ type: "shell_resize", cols, rows }));
      } catch (e) { /* noop */ }
    };
    ws.onmessage = (ev) => {
      try {
        const msg = JSON.parse(ev.data);
        if (msg.type === "shell_output" && typeof msg.data === "string") {
          term.write(msg.data);
          // Auto-run initial command once the prompt (PS1) appears
          if (!sentInitialRef.current && initialCommand && msg.data.match(/[#$]\s*$/)) {
            sentInitialRef.current = true;
            // small delay so the prompt is fully painted
            setTimeout(() => {
              if (ws.readyState === WebSocket.OPEN) {
                ws.send(JSON.stringify({ type: "shell_input", data: initialCommand + "\n" }));
              }
            }, 200);
          }
        }
      } catch (e) { /* noop */ }
    };
    ws.onclose = () => {
      setStatus("disconnected");
      term.write("\r\n\x1b[2m· connessione chiusa\x1b[0m\r\n");
    };
    ws.onerror = () => {
      setStatus("error");
    };

    const onData = term.onData((data) => {
      if (ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({ type: "shell_input", data }));
      }
    });
    const onResize = term.onResize(({ cols, rows }) => {
      if (ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({ type: "shell_resize", cols, rows }));
      }
    });
    const handleWindowResize = () => {
      try { fit.fit(); } catch (e) { /* noop */ }
    };
    window.addEventListener("resize", handleWindowResize);

    return () => {
      window.removeEventListener("resize", handleWindowResize);
      onData.dispose();
      onResize.dispose();
      try { ws.close(); } catch (e) { /* noop */ }
      try { term.dispose(); } catch (e) { /* noop */ }
      termRef.current = null;
      wsRef.current = null;
      fitRef.current = null;
      sentInitialRef.current = false;
    };
  }, [open, rigId, rigName, initialCommand]);

  const startResize = (e) => {
    e.preventDefault();
    dragStateRef.current = { startY: e.clientY, startHeight: panelHeight };
    const onMove = (moveEv) => {
      if (!dragStateRef.current) return;
      const delta = dragStateRef.current.startY - moveEv.clientY;
      const maxHeight = window.innerHeight - 96;
      const next = Math.min(maxHeight, Math.max(MIN_HEIGHT, dragStateRef.current.startHeight + delta));
      setPanelHeight(next);
      try { fitRef.current && fitRef.current.fit(); } catch (err) { /* noop */ }
    };
    const onUp = () => {
      dragStateRef.current = null;
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      setPanelHeight((h) => {
        localStorage.setItem(HEIGHT_STORAGE_KEY, String(h));
        return h;
      });
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  };

  if (!open) return null;

  const StatusIcon = status === "connected" ? Wifi : WifiOff;
  const statusColor = {
    connecting: "text-[#F59E0B]",
    connected: "text-[#10B981]",
    disconnected: "text-[#A1A1AA]",
    error: "text-[#EF4444]",
  }[status];

  return (
    <div
      className="bento p-0 fixed inset-x-0 bottom-[5rem] lg:bottom-0 z-50 flex flex-col rounded-b-none shadow-2xl border-x-0 border-b-0"
      style={{ height: panelHeight }}
      data-testid="terminal-modal"
    >
      <div
        onPointerDown={startResize}
        title="Trascina per ridimensionare"
        className="h-1.5 shrink-0 cursor-row-resize bg-[#27272A] hover:bg-[#10B981]/50 transition-colors"
        data-testid="terminal-resize-handle"
      />
      <div className="flex items-center justify-between px-4 py-3 border-b border-[#27272A] shrink-0">
        <div className="flex items-center gap-3">
          <TerminalIcon className="w-4 h-4 text-[#10B981]" />
          <div className="font-heading font-bold text-sm">
            Terminal · <span className="text-[#10B981]">{rigName}</span>
          </div>
          <div className={`flex items-center gap-1 ml-2 ${statusColor}`}>
            <StatusIcon className="w-3 h-3" />
            <span className="font-mono text-[10px] uppercase tracking-wider">{status}</span>
          </div>
        </div>
        <button
          onClick={onClose}
          data-testid="terminal-close-btn"
          className="text-[#A1A1AA] hover:text-[#FAFAFA] p-1"
        >
          <X className="w-5 h-5" />
        </button>
      </div>
      <div
        ref={containerRef}
        data-testid="terminal-body"
        className="flex-1 bg-[#0B0B0F] p-2 overflow-hidden min-h-0"
      />
      <div className="px-4 py-2 border-t border-[#27272A] font-mono text-[10px] text-[#A1A1AA] flex items-center justify-between shrink-0">
        <span>Shell come root sul rig — Ctrl+C / Ctrl+D / clear funzionano normalmente.</span>
        <span>WS · xterm.js</span>
      </div>
    </div>
  );
}
